import { useEffect, useState } from 'react';

/**
 * The wire format, restated for the browser.
 *
 * Third copy of this shape — the platform has one, the database package has one, this has one — and that
 * is the correct number rather than a failure to factor. They are three programs that deploy separately:
 * a marketplace that requires the platform to be redeployed in step with it is not a marketplace, and a
 * website that imports server code cannot be served as static files.
 */
export type CatalogueEntry = {
  appName: string;
  publisher: string;
  version: string;
  platform: string;
  label: string;
  summary: string;
  color: string;
  tile?: 'badge' | 'bare';
  iconUrl?: string;
  icon?: string;
  source: string;
  permissions: { key: string; label: string; description: string }[];
  osDependencies?: { binary: string; reason: string }[];
  has: { api: boolean; schema: boolean; sidecar: boolean; web: boolean };
  readme?: string;
};

export type Load<T> = { state: 'loading' } | { state: 'error'; message: string } | { state: 'ready'; data: T };

/**
 * Fetch, with the three states kept distinct.
 *
 * Loading, empty and broken are different things and the page says which. An empty catalogue rendered as
 * a spinner looks like a slow site; a broken one rendered as an empty catalogue looks like a store with
 * nothing in it — which is the worst of the three, because it is the only one nobody reports.
 */
function useJson<T>(url: string): Load<T> {
  const [load, setLoad] = useState<Load<T>>({ state: 'loading' });

  useEffect(() => {
    let alive = true;
    setLoad({ state: 'loading' });
    fetch(url)
      .then(async (res) => {
        if (!res.ok) throw new Error(res.status === 404 ? 'Not found' : `The catalogue is unavailable (${res.status})`);
        return (await res.json()) as T;
      })
      .then((data) => alive && setLoad({ state: 'ready', data }))
      .catch((err: unknown) => {
        if (alive) setLoad({ state: 'error', message: err instanceof Error ? err.message : 'Something went wrong' });
      });
    return () => {
      alive = false;
    };
  }, [url]);

  return load;
}

export const useCatalogue = () => useJson<CatalogueEntry[]>('/api/plugins');
export const usePlugin = (appName: string) => useJson<CatalogueEntry>(`/api/plugins/${appName}`);
